import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { overlayActions } from 'store/slices/overlay';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'hooks/useReduxHooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Button from 'components/UI/Button/Button';
import { UserCredentials } from 'interfaces/deployment';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import DeploymentService from 'services/DeploymentService';
import { ReactQueryKeys } from 'constants/react-query-keys';
import { FRONT_ROUTER, DEPLOYMENT_ID_PARAMS_KEY } from 'constants/router';
import { selectUser } from 'store/slices/auth';
import CredentialsFields from 'pages/Deployment/CredentialsFields/CredentialsFields';

/**
 * @description Component for updating deployment from partial to full with credential authentication.
 *
 * @SADtrace SAD-003-6.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @returns {JSX.Element} Rendered component
 */
const UpdateDeploymentModal = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);

  const initialState: UserCredentials = {
    password: '',
    username: user?.email || '',
  };

  const formMethods = useForm<UserCredentials>({
    defaultValues: initialState,
  });
  const { handleSubmit, reset } = formMethods;

  const location = useLocation();

  const backUrl =
    (location.state as string) ?? FRONT_ROUTER.DEPLOYMENT_FULL_DEPLOYMENT_TAB;
  const navigate = useNavigate();

  const { [DEPLOYMENT_ID_PARAMS_KEY]: deployment_id } = useParams();

  // if the id param is not a number, surface an error — in an effect, not during
  // render (calling toast.error during render updates the Toaster mid-render).
  const isInvalidDeploymentId = !/^[0-9]*$/.test(deployment_id ?? '');
  useEffect(() => {
    if (isInvalidDeploymentId) {
      toast.error(t('deployment.errors.invalid__deployment_id'));
    }
  }, [isInvalidDeploymentId, t]);

  const queryClient = useQueryClient();

  /**
   * @description Mutation hook for updating a partial deployment to a full deployment
   * Handles success and error states, including toast notifications and query invalidation
   */
  const { isPending, mutate } = useMutation({
    mutationFn: (data: UserCredentials) =>
      // v8 ignore start -- WAIVER(MICRO): the modal is only opened for an existing deployment row, so deployment_id is always set; '' exists only to satisfy the optional prop type
      DeploymentService.updateToFullDeployment(deployment_id ?? '', data),
    // v8 ignore stop -- WAIVER(MICRO)

    onSuccess: () => {
      toast.success(t('deployment.messages.deployment__success'));
      reset();
      queryClient.invalidateQueries({
        queryKey: [ReactQueryKeys.DEPLOYMENTS],
      });
      queryClient.invalidateQueries({
        queryKey: [ReactQueryKeys.PARTIAL_DEPLOYMENT],
      });
      queryClient.invalidateQueries({
        queryKey: [ReactQueryKeys.CAN_DEPLOY],
      });
      navigate(backUrl);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || t('errors.default'));
    },
  });

  /**
   * @description Opens the overlay when the component mounts
   */
  useEffect(() => {
    dispatch(overlayActions.open({ path: backUrl }));
  }, [dispatch, backUrl]);

  /**
   * @description Handles form submission by triggering the update deployment mutation
   *
   * @param {UserCredentials} data - The form data containing username and password
   */
  const handleFormSubmit: SubmitHandler<UserCredentials> = (data) => {
    mutate(data);
  };

  return (
    <form
      id="formid"
      autoComplete="off"
      className="form-fixed-width"
      onSubmit={handleSubmit(handleFormSubmit)}
    >
      <div className="modal-sidebar-body">
        <CredentialsFields formMethods={formMethods} isModal />
      </div>

      <div className="modal-sidebar-footer">
        <Button
          loading={isPending}
          defaultLabel={t('deployment.buttons.deploy_btn')}
          loadingLabel={t('buttons.updating')}
          type="submit"
          disabled={isPending}
          data-testid="update_deployment_submit_btn"
        ></Button>
        <Link
          to={backUrl}
          className={`btn btn-lg rounded btn-secondary ${
            isPending ? 'disabled' : ''
          }`}
          data-testid="overlay_cancel_btn"
        >
          {t('buttons.cancel')}
        </Link>
      </div>
    </form>
  );
};

export default UpdateDeploymentModal;
